import * as endpoints from 'pkg/api/endpoints/auto';
import * as models from 'pkg/api/models';
import { getPrimaryColorValue } from 'pkg/api/models/group/fmt';
import { hasFeature } from 'pkg/api/models/group/guards';
import { getSplashScreenSettings } from 'pkg/api/models/group/utils';
import * as sdk from 'pkg/core/sdk';

import {
	deleteSplashScreenState,
	storeSplashScreenState,
} from 'components/splash-screen';

interface Invoiced {
	amount: number;
	count: number;
}

export interface InvoicedStatuses {
	open: Invoiced;
	paid: Invoiced;
	pastDue: Invoiced;
	uncollectible: Invoiced;
}

export interface InvoicedDateItems {
	dates: { [key: string]: { currentPeriod: number; lastYear: number } };
	totals: { currentPeriodTotal: number; lastYearTotal: number };
	showAs: 'days' | 'weeks' | 'months';
}

export interface SplashScreenPayload {
	enabled?: boolean;
	image?: string;
}

export interface IPayout {
	amount: number;
	currency: string;
	status: string;
	statusTranslated: string;
	destination: string;
	createdAt: number;
}

export const fetchPayouts = async (groupId: number): Promise<IPayout[]> => {
	const request = await sdk.get(endpoints.Organizations.ListPayouts(groupId));
	const response: IPayout[] = await request.json();

	if (!request.ok) {
		return;
	}

	return response;
};

export async function updateSplashScreen(
	organizationId: number,
	payload: SplashScreenPayload
): Promise<[boolean, SplashScreenPayload]> {
	const request = await sdk.patch(
		endpoints.Organizations.UpdateSplashScreen(organizationId),
		{},
		payload
	);

	if (request.ok) {
		const response = await request.json();

		return [true, response];
	}

	return [false, null];
}

export async function deleteSplashScreen(
	organizationId: number
): Promise<boolean> {
	const request = await sdk.destroy(
		endpoints.Organizations.DeleteSplashScreenImage(organizationId)
	);

	return request.ok;
}

export function hydrateSplashScreenStateFromGroup(group: models.group.Group) {
	const splash = getSplashScreenSettings(group);

	const badgeUrl = group.profileImageUrl ?? '';
	const imageUrl = splash?.imageUrl ?? '';
	const primaryColor = getPrimaryColorValue(group);
	const hasSplashScreenFeature = hasFeature(
		group,
		models.group.Features.SplashScreen
	);

	if (!hasSplashScreenFeature || !splash?.enabled) {
		deleteSplashScreenState();

		return;
	}

	storeSplashScreenState({
		primaryColor,
		badgeUrl,
		imageUrl,
	});
}

interface LokSettings {
	// LOK integration fields
	lokOrganizationNumber: string;
	lokFromTime: number;
}

export async function updateLOKSettings(
	organizationID: number,
	payload: LokSettings
): Promise<boolean> {
	const res = await sdk.patch(
		endpoints.Organizations.UpdateLOKSettings(organizationID),
		{},
		payload
	);
	return res.ok;
}
